import React, { useEffect, useRef, useState } from 'react';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import ProductInfoModal from './ProductInfoModal';
import { ProductName } from '../../config/documentLinks';

import { ConfiguratorState } from '../../types';

interface ProductVisualizerProps {
  state: ConfiguratorState;
}

export default function ProductVisualizer({ state }: ProductVisualizerProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeView, setActiveView] = useState<'top' | 'side' | 'complete'>('top');
  const [showRoughOpening, setShowRoughOpening] = useState(true); // Standard ist jetzt Rohbauöffnung anzeigen
  const [showDimensions, setShowDimensions] = useState(true); // Maße anzeigen
  const [showFrame, setShowFrame] = useState(true); // Rahmen anzeigen
  const [showGlass, setShowGlass] = useState(true); // Glasfläche anzeigen
  const [showAccessories, setShowAccessories] = useState(true); // Zubehör anzeigen
  const [showGrid, setShowGrid] = useState(false); // Raster anzeigen
  const [showTooltip, setShowTooltip] = useState<{[key: string]: boolean}>({});
  const [animationFrame, setAnimationFrame] = useState(0);
  const [isHovering, setIsHovering] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const sideViewCanvasRef = useRef<HTMLCanvasElement>(null);
  const completeViewCanvasRef = useRef<HTMLCanvasElement>(null);
  const isCircular = state.product === 'SkyVision CIRCULAR';
  const isLinear = state.product === 'SkyVision LINEAR';
  const isFixed = state.product === 'SkyVision FIXED';

  // Animation für Glasreflexion
  useEffect(() => {
    const interval = setInterval(() => {
      setAnimationFrame(prev => (prev + 1) % 200);
    }, 50);
    return () => clearInterval(interval);
  }, []);

  // Hauptansicht zeichnen
  useEffect(() => {
    console.log('Hauptansicht zeichnen, showRoughOpening:', showRoughOpening);
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Canvas-Größe setzen
    canvas.width = 600;
    canvas.height = 600;

    // Hintergrund
    ctx.fillStyle = showGrid ? '#ffffff' : '#f8fafc';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Raster zeichnen (optional)
    if (showGrid) {
      ctx.strokeStyle = '#e5e7eb';
      ctx.lineWidth = 1;
      const gridSize = 50;
      
      for (let x = 0; x <= canvas.width; x += gridSize) {
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, canvas.height);
        ctx.stroke();
      }
      
      for (let y = 0; y <= canvas.height; y += gridSize) {
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(canvas.width, y);
        ctx.stroke();
      }
    }

    // Schatten
    // Schatten-Effekte entfernt für sauberes Design
    ctx.shadowColor = 'transparent';
    ctx.shadowBlur = 0;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 0;

    if (isLinear) {
      // LINEAR Modul-Visualisierung
      const moduleCount = state.modules.fixed + state.modules.opening;
      const moduleWidth = 1000; // 1000mm pro Modul
      const moduleLength = 500; // 500mm Standardbreite
      
      const totalWidth = moduleWidth * moduleCount;
      const scale = Math.min(
        (canvas.width - 100) / totalWidth,
        (canvas.height - 100) / moduleLength
      );

      const startX = (canvas.width - totalWidth * scale) / 2;
      const startY = (canvas.height - moduleLength * scale) / 2;

      // Module zeichnen
      for (let i = 0; i < moduleCount; i++) {
        const moduleX = startX + (i * moduleWidth * scale);
        const isOpeningModule = i < state.modules.opening;
        
        // Äußerer Rahmen (nur wenn showFrame aktiv)
        if (showFrame) {
          ctx.fillStyle = '#475569';
          ctx.fillRect(
            moduleX - 10,
            startY - 10,
            moduleWidth * scale + 20,
            moduleLength * scale + 20
          );

          // Innerer Rahmen
          ctx.fillStyle = isOpeningModule ? '#cbd5e1' : '#e2e8f0';
          ctx.fillRect(
            moduleX - 5,
            startY - 5,
            moduleWidth * scale + 10,
            moduleLength * scale + 10
          );
        }

        // Glasfläche (nur wenn showGlass aktiv)
        if (showGlass) {
          // Farbiger Rahmen um die Glasfläche
          ctx.strokeStyle = '#0ea5e9';
          ctx.lineWidth = 3;
          ctx.strokeRect(moduleX - 1, startY - 1, moduleWidth * scale + 2, moduleLength * scale + 2);
          
          ctx.shadowColor = 'rgba(14, 165, 233, 0.3)';
          ctx.shadowBlur = 15;
          switch (state.glassType) {
            case 'standard':
              ctx.fillStyle = 'rgba(56, 189, 248, 0.6)';
              break;
            case 'premium':
              ctx.fillStyle = 'rgba(14, 165, 233, 0.7)';
              break;
            default:
              ctx.fillStyle = 'rgba(125, 211, 252, 0.5)';
          }
          ctx.fillRect(moduleX, startY, moduleWidth * scale, moduleLength * scale);
          ctx.shadowColor = 'transparent';
          ctx.shadowBlur = 0;
          
          // Glasstruktur-Muster
          ctx.strokeStyle = 'rgba(255, 255, 255, 0.15)';
          ctx.lineWidth = 1;
          for (let i = 0; i < moduleLength * scale; i += 30) {
            ctx.beginPath();
            ctx.moveTo(moduleX, startY + i);
            ctx.lineTo(moduleX + moduleWidth * scale, startY + i);
            ctx.stroke();
          }
        } else if (!showFrame) {
          // Wenn weder Rahmen noch Glas angezeigt wird, zeige Umriss
          ctx.strokeStyle = '#94a3b8';
          ctx.lineWidth = 2;
          ctx.strokeRect(moduleX, startY, moduleWidth * scale, moduleLength * scale);
        }

        // Animierte Reflexion
        const animOffset = (animationFrame / 200) * (moduleWidth * scale + moduleLength * scale);
        const gradient = ctx.createLinearGradient(
          moduleX - animOffset,
          startY - animOffset,
          moduleX + moduleWidth * scale + animOffset,
          startY + moduleLength * scale + animOffset
        );
        gradient.addColorStop(0, 'rgba(255, 255, 255, 0)');
        gradient.addColorStop(0.4, 'rgba(255, 255, 255, 0)');
        gradient.addColorStop(0.5, 'rgba(255, 255, 255, 0.4)');
        gradient.addColorStop(0.6, 'rgba(255, 255, 255, 0)');
        gradient.addColorStop(1, 'rgba(255, 255, 255, 0)');
        ctx.fillStyle = gradient;
        ctx.fillRect(moduleX, startY, moduleWidth * scale, moduleLength * scale);
        
        // Hover-Effekt
        if (isHovering) {
          ctx.fillStyle = 'rgba(14, 165, 233, 0.2)';
          ctx.fillRect(moduleX, startY, moduleWidth * scale, moduleLength * scale);
        }

        // Öffnungsrichtung für öffenbare Module
        if (isOpeningModule) {
          ctx.strokeStyle = '#475569';
          ctx.lineWidth = 2;
          ctx.beginPath();
          ctx.moveTo(moduleX + 20, startY + 20);
          ctx.lineTo(moduleX + moduleWidth * scale - 20, startY + 20);
          ctx.stroke();

          // Pfeil
          ctx.beginPath();
          ctx.moveTo(moduleX + moduleWidth * scale - 30, startY + 10);
          ctx.lineTo(moduleX + moduleWidth * scale - 20, startY + 20);
          ctx.lineTo(moduleX + moduleWidth * scale - 30, startY + 30);
          ctx.stroke();
        }
      }

      // Maße anzeigen (nur wenn showDimensions aktiv)
      if (showDimensions) {
        ctx.fillStyle = '#1e293b';
        ctx.font = '14px Arial';
        ctx.textAlign = 'center';
        
        // Gesamtbreite
        ctx.fillText(
          `${totalWidth} mm`,
          startX + (totalWidth * scale) / 2,
          startY + moduleLength * scale + 40
        );
        
        // Modulbreite
        ctx.save();
        ctx.translate(startX - 40, startY + (moduleLength * scale) / 2);
        ctx.rotate(-Math.PI / 2);
        ctx.fillText(`${moduleLength} mm`, 0, 0);
        ctx.restore();
      }

    } else if (isCircular) {
      // Maßstab für rundes Fenster berechnen
      const diameter = state.dimensions.diameter || 600;
      const scale = (canvas.width - 100) / diameter;
      const radius = (diameter * scale) / 2;
      const centerX = canvas.width / 2;
      const centerY = canvas.height / 2;

      // Äußerer Rahmen (rund) - nur wenn showFrame aktiv
      if (showFrame) {
        ctx.beginPath();
        ctx.arc(centerX, centerY, radius + 20, 0, Math.PI * 2);
        ctx.fillStyle = '#475569'; // skyvision-600
        ctx.fill();

        // Innerer Rahmen (rund)
        ctx.beginPath();
        ctx.arc(centerX, centerY, radius + 10, 0, Math.PI * 2);
        ctx.fillStyle = '#e2e8f0'; // skyvision-200
        ctx.fill();
      }

      // Glasfläche (rund) - nur wenn showGlass aktiv
      if (showGlass) {
        // Farbiger Rahmen um die Glasfläche
        ctx.strokeStyle = '#0ea5e9';
        ctx.lineWidth = 4;
        ctx.shadowColor = 'rgba(14, 165, 233, 0.4)';
        ctx.shadowBlur = 20;
        ctx.beginPath();
        ctx.arc(centerX, centerY, radius + 2, 0, Math.PI * 2);
        ctx.stroke();
        
        ctx.shadowColor = 'rgba(14, 165, 233, 0.3)';
        ctx.shadowBlur = 15;
        ctx.beginPath();
        ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
        switch (state.glassType) {
          case 'klar':
            ctx.fillStyle = 'rgba(56, 189, 248, 0.6)';
            break;
          case 'matt':
            ctx.fillStyle = 'rgba(14, 165, 233, 0.7)';
            break;
          case 'selbstreinigend':
            ctx.fillStyle = 'rgba(125, 211, 252, 0.5)';
            break;
          default:
            ctx.fillStyle = 'rgba(125, 211, 252, 0.5)';
        }
        ctx.fill();
        ctx.shadowColor = 'transparent';
        ctx.shadowBlur = 0;
        
        // Glasstruktur-Muster (konzentrische Kreise)
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.15)';
        ctx.lineWidth = 1;
        for (let r = 30; r < radius; r += 30) {
          ctx.beginPath();
          ctx.arc(centerX, centerY, r, 0, Math.PI * 2);
          ctx.stroke();
        }
      } else if (!showFrame) {
        // Wenn weder Rahmen noch Glas angezeigt wird, zeige Umriss
        ctx.strokeStyle = '#94a3b8';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
        ctx.stroke();
      }

      // Animierte Reflexion (rund)
      const animAngle = (animationFrame / 200) * Math.PI * 2;
      const gradientCircle = ctx.createRadialGradient(
        centerX + Math.cos(animAngle) * radius/3,
        centerY + Math.sin(animAngle) * radius/3,
        0,
        centerX,
        centerY,
        radius
      );
      gradientCircle.addColorStop(0, 'rgba(255, 255, 255, 0.5)');
      gradientCircle.addColorStop(0.3, 'rgba(255, 255, 255, 0.3)');
      gradientCircle.addColorStop(1, 'rgba(255, 255, 255, 0)');
      ctx.beginPath();
      ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
      ctx.fillStyle = gradientCircle;
      ctx.fill();
      
      // Hover-Effekt
      if (isHovering) {
        ctx.beginPath();
        ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(14, 165, 233, 0.2)';
        ctx.fill();
      }

      // Durchmesser anzeigen (nur wenn showDimensions aktiv)
      if (showDimensions) {
        ctx.fillStyle = '#1e293b';
        ctx.font = '14px Arial';
        ctx.textAlign = 'center';
        ctx.fillText(
          `Ø ${diameter} mm`,
          centerX,
          centerY + radius + 40
        );
      }

    } else {
      // Rechteckiges Fenster wie bisher
      const width = state.dimensions.width || 1000;
      const length = state.dimensions.length || 1000;
      
      // Rohbauöffnung berechnen (Bestellmaß + 30mm)
      const roughOpeningWidth = width + 30;
      const roughOpeningLength = length + 30;
      
      const scale = Math.min(
        (canvas.width - 100) / width,
        (canvas.height - 100) / length
      );

      const fensterX = (canvas.width - width * scale) / 2;
      const fensterY = (canvas.height - length * scale) / 2;

      // Äußerer Rahmen (nur wenn showFrame aktiv)
      if (showFrame) {
        ctx.fillStyle = '#475569';
        ctx.fillRect(
          fensterX - 20,
          fensterY - 20,
          width * scale + 40,
          length * scale + 40
        );

        // Innerer Rahmen
        ctx.fillStyle = '#e2e8f0';
        ctx.fillRect(
          fensterX - 10,
          fensterY - 10,
          width * scale + 20,
          length * scale + 20
        );
      }

      // Glasfläche (nur wenn showGlass aktiv)
      if (showGlass) {
        // Farbiger Rahmen um die Glasfläche
        ctx.strokeStyle = '#0ea5e9';
        ctx.lineWidth = 4;
        ctx.shadowColor = 'rgba(14, 165, 233, 0.4)';
        ctx.shadowBlur = 20;
        ctx.strokeRect(fensterX - 2, fensterY - 2, width * scale + 4, length * scale + 4);
        
        ctx.shadowColor = 'rgba(14, 165, 233, 0.3)';
        ctx.shadowBlur = 15;
        switch (state.glassType) {
          case 'klar':
            ctx.fillStyle = 'rgba(56, 189, 248, 0.6)';
            break;
          case 'matt':
            ctx.fillStyle = 'rgba(14, 165, 233, 0.7)';
            break;
          case 'selbstreinigend':
            ctx.fillStyle = 'rgba(125, 211, 252, 0.5)';
            break;
          default:
            ctx.fillStyle = 'rgba(125, 211, 252, 0.5)';
        }
        ctx.fillRect(fensterX, fensterY, width * scale, length * scale);
        ctx.shadowColor = 'transparent';
        ctx.shadowBlur = 0;
        
        // Glasstruktur-Muster (Raster)
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.15)';
        ctx.lineWidth = 1;
        for (let i = 0; i < width * scale; i += 30) {
          ctx.beginPath();
          ctx.moveTo(fensterX + i, fensterY);
          ctx.lineTo(fensterX + i, fensterY + length * scale);
          ctx.stroke();
        }
        for (let i = 0; i < length * scale; i += 30) {
          ctx.beginPath();
          ctx.moveTo(fensterX, fensterY + i);
          ctx.lineTo(fensterX + width * scale, fensterY + i);
          ctx.stroke();
        }
      } else if (!showFrame) {
        // Wenn weder Rahmen noch Glas angezeigt wird, zeige Umriss
        ctx.strokeStyle = '#94a3b8';
        ctx.lineWidth = 2;
        ctx.strokeRect(fensterX, fensterY, width * scale, length * scale);
      }

      // Animierte Reflexion
      const animOffset = (animationFrame / 200) * (width * scale + length * scale);
      const gradient = ctx.createLinearGradient(
        fensterX - animOffset,
        fensterY - animOffset,
        fensterX + width * scale + animOffset,
        fensterY + length * scale + animOffset
      );
      gradient.addColorStop(0, 'rgba(255, 255, 255, 0)');
      gradient.addColorStop(0.4, 'rgba(255, 255, 255, 0)');
      gradient.addColorStop(0.5, 'rgba(255, 255, 255, 0.5)');
      gradient.addColorStop(0.6, 'rgba(255, 255, 255, 0)');
      gradient.addColorStop(1, 'rgba(255, 255, 255, 0)');
      ctx.fillStyle = gradient;
      ctx.fillRect(fensterX, fensterY, width * scale, length * scale);
      
      // Hover-Effekt
      if (isHovering) {
        ctx.fillStyle = 'rgba(14, 165, 233, 0.2)';
        ctx.fillRect(fensterX, fensterY, width * scale, length * scale);
      }

      // Wenn Rohbauöffnung angezeigt werden soll und es sich um FIXED handelt
      if (showRoughOpening && isFixed) {
        // Gestrichelte Linie für Rohbauöffnung zeichnen
        ctx.strokeStyle = '#ef4444'; // Rot für Rohbauöffnung
        ctx.lineWidth = 2;
        ctx.setLineDash([5, 5]);
        
        // Berechne Position für Rohbauöffnung (15mm Abstand auf jeder Seite)
        const roughX = fensterX - 15 * scale;
        const roughY = fensterY - 15 * scale;
        const roughWidth = roughOpeningWidth * scale;
        const roughHeight = roughOpeningLength * scale;
        
        // Zeichne Rohbauöffnung
        ctx.strokeRect(roughX, roughY, roughWidth, roughHeight);
        ctx.setLineDash([]);
      }

      // Maße anzeigen (nur wenn showDimensions aktiv)
      if (showDimensions) {
        ctx.fillStyle = '#1e293b';
        ctx.font = '14px Arial';
        ctx.textAlign = 'center';
        
        // Breite
        if (showRoughOpening && isFixed) {
          ctx.fillStyle = '#ef4444';
          ctx.fillText(
            `${roughOpeningWidth} mm (Rohbau)`,
            fensterX + (width * scale) / 2,
            fensterY + length * scale + 40
          );
          ctx.fillStyle = '#1e293b';
          ctx.fillText(
            `${width} mm (Bestellmaß)`,
            fensterX + (width * scale) / 2,
            fensterY + length * scale + 60
          );
        } else {
        ctx.fillText(
          `${width} mm`,
          fensterX + (width * scale) / 2,
          fensterY + length * scale + 40
        );
        }
        
        // Länge
        ctx.save();
        if (showRoughOpening && isFixed) {
          ctx.fillStyle = '#ef4444';
          ctx.translate(fensterX - 60, fensterY + (length * scale) / 2);
          ctx.rotate(-Math.PI / 2);
          ctx.fillText(`${roughOpeningLength} mm (Rohbau)`, 0, 0);
          
          ctx.fillStyle = '#1e293b';
          ctx.translate(20, 0);
          ctx.fillText(`${length} mm (Bestellmaß)`, 0, 0);
        } else {
        ctx.translate(fensterX - 40, fensterY + (length * scale) / 2);
        ctx.rotate(-Math.PI / 2);
        ctx.fillText(`${length} mm`, 0, 0);
        }
        ctx.restore();
      }
    }

    // Zubehör zeichnen (nur wenn showAccessories aktiv)
    if (showAccessories) {
      // Gemeinsame Variablen für alle Zubehörteile
      const width = isCircular ? (state.dimensions.diameter || 600) : (state.dimensions.width || 1000);
      const length = isCircular ? (state.dimensions.diameter || 600) : (state.dimensions.length || 1000);
      const scale = isCircular 
        ? (canvas.width - 100) / (state.dimensions.diameter || 600)
        : Math.min((canvas.width - 100) / width, (canvas.height - 100) / length);
      const centerX = canvas.width / 2;
      const centerY = canvas.height / 2;
      const fensterX = (canvas.width - width * scale) / 2;
      const fensterY = (canvas.height - length * scale) / 2;

      // 1. SONNENSCHUTZ
      if (state.accessories.includes('sonnenschutz')) {
        if (isCircular) {
          const radius = ((state.dimensions.diameter || 600) * scale) / 2;
          ctx.strokeStyle = '#94a3b8';
          ctx.setLineDash([5, 5]);
          for (let angle = 0; angle < Math.PI * 2; angle += Math.PI / 12) {
            ctx.beginPath();
            ctx.moveTo(centerX, centerY);
            ctx.lineTo(
              centerX + Math.cos(angle) * radius,
              centerY + Math.sin(angle) * radius
            );
            ctx.stroke();
          }
          ctx.setLineDash([]);
        } else {
          ctx.strokeStyle = '#94a3b8';
          ctx.setLineDash([5, 5]);
          for (let i = 0; i < width * scale; i += 20) {
            ctx.beginPath();
            ctx.moveTo(fensterX + i, fensterY);
            ctx.lineTo(fensterX + i, fensterY + length * scale);
            ctx.stroke();
          }
          ctx.setLineDash([]);
        }
      }

      // 2. LED-BELEUCHTUNG
      if (state.accessories.includes('led')) {
        if (isCircular) {
          const radius = ((state.dimensions.diameter || 600) * scale) / 2;
          ctx.beginPath();
          ctx.arc(centerX, centerY, radius + 5, 0, Math.PI * 2);
          ctx.fillStyle = 'rgba(250, 204, 21, 0.3)';
          ctx.fill();
        } else {
          ctx.fillStyle = 'rgba(250, 204, 21, 0.3)';
          ctx.fillRect(
            fensterX - 5,
            fensterY - 5,
            width * scale + 10,
            length * scale + 10
          );
        }
      }

      // 3. MOTOREN (für COMFORT)
      const hasMotorSingle = state.accessories.includes('motor_single');
      const hasMotorDual = state.accessories.includes('motor_dual');
      
      if (hasMotorSingle || hasMotorDual) {
        const motorCount = hasMotorDual ? 2 : 1;
        
        if (isCircular) {
          // Motor(en) für rundes Fenster - am Rand positioniert
          const radius = ((state.dimensions.diameter || 600) * scale) / 2;
          const motorSize = 15;
          
          if (motorCount === 1) {
            // Ein Motor oben rechts
            const motorX = centerX + radius * 0.7;
            const motorY = centerY - radius * 0.7;
            
            // Motor-Symbol (Rechteck mit "M")
            ctx.fillStyle = '#ef4444';
            ctx.fillRect(motorX - motorSize/2, motorY - motorSize/2, motorSize, motorSize);
            ctx.strokeStyle = '#991b1b';
            ctx.lineWidth = 2;
            ctx.strokeRect(motorX - motorSize/2, motorY - motorSize/2, motorSize, motorSize);
            
            ctx.fillStyle = '#ffffff';
            ctx.font = 'bold 12px Arial';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText('M', motorX, motorY);
          } else {
            // Zwei Motoren gegenüberliegend
            const positions = [
              { x: centerX + radius * 0.7, y: centerY - radius * 0.7 },
              { x: centerX - radius * 0.7, y: centerY + radius * 0.7 }
            ];
            
            positions.forEach((pos, index) => {
              ctx.fillStyle = '#ef4444';
              ctx.fillRect(pos.x - motorSize/2, pos.y - motorSize/2, motorSize, motorSize);
              ctx.strokeStyle = '#991b1b';
              ctx.lineWidth = 2;
              ctx.strokeRect(pos.x - motorSize/2, pos.y - motorSize/2, motorSize, motorSize);
              
              ctx.fillStyle = '#ffffff';
              ctx.font = 'bold 10px Arial';
              ctx.textAlign = 'center';
              ctx.textBaseline = 'middle';
              ctx.fillText(`M${index + 1}`, pos.x, pos.y);
            });
          }
        } else {
          // Motor(en) für rechteckiges Fenster - an der Seite positioniert
          const motorWidth = 20;
          const motorHeight = 30;
          
          if (motorCount === 1) {
            // Ein Motor rechts in der Mitte
            const motorX = fensterX + width * scale + 10;
            const motorY = fensterY + (length * scale) / 2 - motorHeight / 2;
            
            ctx.fillStyle = '#ef4444';
            ctx.fillRect(motorX, motorY, motorWidth, motorHeight);
            ctx.strokeStyle = '#991b1b';
            ctx.lineWidth = 2;
            ctx.strokeRect(motorX, motorY, motorWidth, motorHeight);
            
            ctx.fillStyle = '#ffffff';
            ctx.font = 'bold 12px Arial';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText('M', motorX + motorWidth/2, motorY + motorHeight/2);
          } else {
            // Zwei Motoren an den Seiten
            const positions = [
              { x: fensterX + width * scale + 10, y: fensterY + (length * scale) / 2 - motorHeight / 2 },
              { x: fensterX - motorWidth - 10, y: fensterY + (length * scale) / 2 - motorHeight / 2 }
            ];
            
            positions.forEach((pos, index) => {
              ctx.fillStyle = '#ef4444';
              ctx.fillRect(pos.x, pos.y, motorWidth, motorHeight);
              ctx.strokeStyle = '#991b1b';
              ctx.lineWidth = 2;
              ctx.strokeRect(pos.x, pos.y, motorWidth, motorHeight);
              
              ctx.fillStyle = '#ffffff';
              ctx.font = 'bold 10px Arial';
              ctx.textAlign = 'center';
              ctx.textBaseline = 'middle';
              ctx.fillText(`M${index + 1}`, pos.x + motorWidth/2, pos.y + motorHeight/2);
            });
          }
        }
      }

      // 4. STEUERUNGEN (WCC103 oder WCC106)
      const hasControl103 = state.accessories.includes('wcc103');
      const hasControl106 = state.accessories.includes('wcc106');
      
      if (hasControl103 || hasControl106) {
        const controlSize = 25;
        
        if (isCircular) {
          // Steuerung unten links außerhalb des Fensters
          const radius = ((state.dimensions.diameter || 600) * scale) / 2;
          const controlX = centerX - radius - 40;
          const controlY = centerY + radius - 20;
          
          // Steuerungs-Symbol (Quadrat mit Icon)
          ctx.fillStyle = '#3b82f6';
          ctx.fillRect(controlX - controlSize/2, controlY - controlSize/2, controlSize, controlSize);
          ctx.strokeStyle = '#1e40af';
          ctx.lineWidth = 2;
          ctx.strokeRect(controlX - controlSize/2, controlY - controlSize/2, controlSize, controlSize);
          
          // Icon: Fernbedienungs-Symbol
          ctx.fillStyle = '#ffffff';
          ctx.font = 'bold 16px Arial';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText('⚡', controlX, controlY);
        } else {
          // Steuerung unten links außerhalb des Fensters
          const controlX = fensterX - 40;
          const controlY = fensterY + length * scale - 20;
          
          ctx.fillStyle = '#3b82f6';
          ctx.fillRect(controlX - controlSize/2, controlY - controlSize/2, controlSize, controlSize);
          ctx.strokeStyle = '#1e40af';
          ctx.lineWidth = 2;
          ctx.strokeRect(controlX - controlSize/2, controlY - controlSize/2, controlSize, controlSize);
          
          ctx.fillStyle = '#ffffff';
          ctx.font = 'bold 16px Arial';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText('⚡', controlX, controlY);
        }
      }

      // 5. REGENSENSOR
      if (state.accessories.includes('regensensor') || state.accessories.includes('rain_sensor')) {
        const sensorSize = 12;
        
        if (isCircular) {
          // Sensor oben mittig auf dem Glas
          const sensorX = centerX;
          const sensorY = centerY - ((state.dimensions.diameter || 600) * scale) / 2 + 30;
          
          ctx.fillStyle = '#06b6d4';
          ctx.beginPath();
          ctx.arc(sensorX, sensorY, sensorSize/2, 0, Math.PI * 2);
          ctx.fill();
          ctx.strokeStyle = '#0e7490';
          ctx.lineWidth = 2;
          ctx.stroke();
          
          ctx.fillStyle = '#ffffff';
          ctx.font = 'bold 10px Arial';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText('R', sensorX, sensorY);
        } else {
          // Sensor oben mittig auf dem Glas
          const sensorX = fensterX + (width * scale) / 2;
          const sensorY = fensterY + 30;
          
          ctx.fillStyle = '#06b6d4';
          ctx.beginPath();
          ctx.arc(sensorX, sensorY, sensorSize/2, 0, Math.PI * 2);
          ctx.fill();
          ctx.strokeStyle = '#0e7490';
          ctx.lineWidth = 2;
          ctx.stroke();
          
          ctx.fillStyle = '#ffffff';
          ctx.font = 'bold 10px Arial';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText('R', sensorX, sensorY);
        }
      }

      // 6. WÄRMESCHUTZGLAS / SELBSTREINIGENDES GLAS (visueller Hinweis)
      if (state.accessories.includes('waermeschutz') || state.accessories.includes('heat_protection')) {
        // Leichter orange Schimmer für Wärmeschutz
        if (isCircular) {
          const radius = ((state.dimensions.diameter || 600) * scale) / 2;
          ctx.beginPath();
          ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
          ctx.fillStyle = 'rgba(251, 146, 60, 0.15)';
          ctx.fill();
        } else {
          ctx.fillStyle = 'rgba(251, 146, 60, 0.15)';
          ctx.fillRect(fensterX, fensterY, width * scale, length * scale);
        }
      }

      if (state.accessories.includes('selbstreinigend') || state.accessories.includes('self_cleaning')) {
        // Leichter grüner Schimmer für selbstreinigendes Glas
        if (isCircular) {
          const radius = ((state.dimensions.diameter || 600) * scale) / 2;
          ctx.beginPath();
          ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
          ctx.fillStyle = 'rgba(34, 197, 94, 0.15)';
          ctx.fill();
        } else {
          ctx.fillStyle = 'rgba(34, 197, 94, 0.15)';
          ctx.fillRect(fensterX, fensterY, width * scale, length * scale);
        }
      }
    }

  }, [state, isCircular, isLinear, activeView, showRoughOpening, showDimensions, showFrame, showGlass, showAccessories, showGrid, isFixed, animationFrame, isHovering]);

  // Seitenansicht mit Unterkonstruktion zeichnen
  useEffect(() => {
    console.log('Seitenansicht zeichnen, showRoughOpening:', showRoughOpening);
    const canvas = sideViewCanvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Canvas-Größe setzen
    canvas.width = 600;
    canvas.height = 200;

    // Hintergrund
    ctx.fillStyle = '#f8fafc';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Maßstab berechnen
    const length = isCircular ? (state.dimensions.diameter || 600) : (state.dimensions.length || 1000); // Tiefe/Länge für die Seitenansicht
    const height = state.dimensions.height || 300; // Höhe der Unterkonstruktion
    
    // Rohbauöffnung berechnen (Bestellmaß + 30mm)
    const roughOpeningLength = isFixed ? length + 30 : length;
    
    // Skalierung und Position berechnen
    const scale = Math.min(
      (canvas.width - 100) / length,
      (canvas.height - 60) / height
    );

    const startX = (canvas.width - length * scale) / 2;
    const groundY = canvas.height - 30;
    
    // Wenn Rohbauöffnung angezeigt werden soll, zeichne gestrichelte Linien
    if (showRoughOpening && isFixed) {
      // Zeichne die Rohbauöffnung als gestrichelte rote Linie
      const roughScale = Math.min(
        (canvas.width - 100) / roughOpeningLength,
        (canvas.height - 60) / height
      );
      const roughStartX = (canvas.width - roughOpeningLength * roughScale) / 2;
      
      // Gestrichelte Linie für die Rohbauöffnung
      ctx.strokeStyle = '#ef4444'; // Rot
      ctx.lineWidth = 2;
      ctx.setLineDash([5, 5]);
      
      // Vertikale Linien für die Rohbauöffnung
      ctx.beginPath();
      ctx.moveTo(roughStartX, groundY);
      ctx.lineTo(roughStartX, groundY - height * scale - 15);
      ctx.stroke();
      
      ctx.beginPath();
      ctx.moveTo(roughStartX + roughOpeningLength * roughScale, groundY);
      ctx.lineTo(roughStartX + roughOpeningLength * roughScale, groundY - height * scale - 15);
      ctx.stroke();
      
      // Horizontale Linie für die Rohbauöffnung oben
      ctx.beginPath();
      ctx.moveTo(roughStartX, groundY - height * scale - 15);
      ctx.lineTo(roughStartX + roughOpeningLength * roughScale, groundY - height * scale - 15);
      ctx.stroke();
      
      ctx.setLineDash([]);
      
      // Beschriftung für Rohbauöffnung
      ctx.fillStyle = '#ef4444';
      ctx.fillText('Rohbauöffnung', (roughStartX + roughStartX + roughOpeningLength * roughScale) / 2, groundY - height * scale - 30);
    }
    
    // Boden zeichnen
    ctx.fillStyle = '#94a3b8';
    ctx.fillRect(20, groundY, canvas.width - 40, 20);
    
    // Unterkonstruktion zeichnen
    ctx.fillStyle = '#475569';
    const constructionX = startX - 10;
    const constructionWidth = length * scale + 20;
    const constructionHeight = height * scale;
    ctx.fillRect(constructionX, groundY - constructionHeight, constructionWidth, constructionHeight);
    
    // Glasfläche oben zeichnen mit Effekten
    ctx.fillStyle = '#e2e8f0';
    ctx.fillRect(startX - 5, groundY - constructionHeight - 10, length * scale + 10, 10);
    
    // Farbiger Rahmen
    ctx.strokeStyle = '#0ea5e9';
    ctx.lineWidth = 2;
    ctx.strokeRect(startX - 1, groundY - constructionHeight - 11, length * scale + 2, 12);
    
    ctx.shadowColor = 'rgba(14, 165, 233, 0.3)';
    ctx.shadowBlur = 10;
    switch (state.glassType) {
      case 'klar':
        ctx.fillStyle = 'rgba(56, 189, 248, 0.6)';
        break;
      case 'matt':
        ctx.fillStyle = 'rgba(14, 165, 233, 0.7)';
        break;
      case 'selbstreinigend':
        ctx.fillStyle = 'rgba(125, 211, 252, 0.5)';
        break;
      default:
        ctx.fillStyle = 'rgba(125, 211, 252, 0.5)';
    }
    ctx.fillRect(startX, groundY - constructionHeight - 10, length * scale, 10);
    ctx.shadowColor = 'transparent';
    ctx.shadowBlur = 0;
    
    // Maße anzeigen
    ctx.fillStyle = '#1e293b';
    ctx.font = '14px Arial';
    ctx.textAlign = 'center';
    
    // Länge/Tiefe
    if (showRoughOpening && isFixed) {
      ctx.fillStyle = '#ef4444';
      ctx.fillText(
        `${roughOpeningLength} mm (Rohbau)`,
        startX + (length * scale) / 2,
        groundY + 15
      );
      ctx.fillStyle = '#1e293b';
      ctx.fillText(
        `${length} mm (Bestellmaß)`,
        startX + (length * scale) / 2,
        groundY + 35
      );
    } else {
      ctx.fillText(
        `${length} mm (Tiefe)`,
        startX + (length * scale) / 2,
        groundY + 15
      );
    }
    
    // Höhe
    ctx.save();
    ctx.translate(constructionX - 20, groundY - constructionHeight / 2);
    ctx.rotate(-Math.PI / 2);
    ctx.fillText(`${height} mm (Höhe)`, 0, 0);
    ctx.restore();
    
  }, [state, isCircular, isLinear, activeView, showRoughOpening, showDimensions, isFixed, animationFrame]);

  // Komplettansicht zeichnen (isometrische Darstellung)
  useEffect(() => {
    const canvas = completeViewCanvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Canvas-Größe setzen
    canvas.width = 600;
    canvas.height = 400;

    // Hintergrund
    ctx.fillStyle = '#f8fafc';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Maße für die isometrische Darstellung
    const width = isCircular ? (state.dimensions.diameter || 600) : (state.dimensions.width || 1000);
    const length = isCircular ? (state.dimensions.diameter || 600) : (state.dimensions.length || 1000);
    const height = state.dimensions.height || (isCircular ? 240 : 300); // CIRCULAR: 240mm, FIXED: 300mm
    
    // Rohbauöffnung berechnen (Bestellmaß + 30mm) für FIXED
    const roughOpeningWidth = isFixed ? width + 30 : width;
    const roughOpeningLength = isFixed ? length + 30 : length;

    // Skalierung für die isometrische Ansicht
    const scale = Math.min(
      (canvas.width - 150) / (width + length),
      (canvas.height - 100) / (height + length/2)
    ) * 0.7;

    // Isometrische Projektionswinkel
    const isoAngle = Math.PI / 6; // 30 Grad
    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2 + 50; // Korrektur der Position nach unten

    // Hilfsfunktion für isometrische Projektion
    const isoProject = (x: number, y: number, z: number) => {
      return {
        x: centerX + (x - z) * Math.cos(isoAngle) * scale,
        y: centerY + (y * scale) - (x + z) * Math.sin(isoAngle) * scale
      };
    };

    // Boden zeichnen
    ctx.fillStyle = '#94a3b8';
    ctx.beginPath();
    const ground1 = isoProject(-width/2 - 100, 0, -length/2 - 100);
    const ground2 = isoProject(width/2 + 100, 0, -length/2 - 100);
    const ground3 = isoProject(width/2 + 100, 0, length/2 + 100);
    const ground4 = isoProject(-width/2 - 100, 0, length/2 + 100);
    ctx.moveTo(ground1.x, ground1.y);
    ctx.lineTo(ground2.x, ground2.y);
    ctx.lineTo(ground3.x, ground3.y);
    ctx.lineTo(ground4.x, ground4.y);
    ctx.closePath();
    ctx.fill();

    // Unterkonstruktion zeichnen
    const baseWidth = width + 40;
    const baseLength = length + 40;

    // Vorderseite der Unterkonstruktion
    ctx.fillStyle = '#475569';
    ctx.beginPath();
    const base1 = isoProject(-baseWidth/2, 0, -baseLength/2);
    const base2 = isoProject(baseWidth/2, 0, -baseLength/2);
    const base3 = isoProject(baseWidth/2, -height, -baseLength/2);
    const base4 = isoProject(-baseWidth/2, -height, -baseLength/2);
    ctx.moveTo(base1.x, base1.y);
    ctx.lineTo(base2.x, base2.y);
    ctx.lineTo(base3.x, base3.y);
    ctx.lineTo(base4.x, base4.y);
    ctx.closePath();
    ctx.fill();

    // Rechte Seite der Unterkonstruktion
    ctx.fillStyle = '#334155';
    ctx.beginPath();
    const base5 = isoProject(baseWidth/2, 0, baseLength/2);
    const base6 = isoProject(baseWidth/2, -height, baseLength/2);
    ctx.moveTo(base2.x, base2.y);
    ctx.lineTo(base5.x, base5.y);
    ctx.lineTo(base6.x, base6.y);
    ctx.lineTo(base3.x, base3.y);
    ctx.closePath();
    ctx.fill();

    // Oberseite der Unterkonstruktion
    ctx.fillStyle = '#1e293b';
    ctx.beginPath();
    const base7 = isoProject(-baseWidth/2, -height, baseLength/2);
    ctx.moveTo(base4.x, base4.y);
    ctx.lineTo(base3.x, base3.y);
    ctx.lineTo(base6.x, base6.y);
    ctx.lineTo(base7.x, base7.y);
    ctx.closePath();
    ctx.fill();

    // Glasfläche
    const glassWidth = width + 20;
    const glassLength = length + 20;

    // Rahmen der Glasfläche
    ctx.fillStyle = '#e2e8f0';
    
    if (isCircular) {
      // Für CIRCULAR: Runder Rahmen (Ellipse)
      const frameRadius = (width + 20) / 2;
      const frameCenterPoint = isoProject(0, -height-10, 0);
      const frameRadiusX = frameRadius * Math.cos(isoAngle) * scale;
      const frameRadiusY = frameRadius * Math.sin(isoAngle) * scale;
      
      ctx.beginPath();
      ctx.ellipse(frameCenterPoint.x, frameCenterPoint.y, frameRadiusX, frameRadiusY, 0, 0, Math.PI * 2);
      ctx.fill();
    } else {
      // Für FIXED/LINEAR: Rechteckiger Rahmen
      ctx.beginPath();
      const glass1 = isoProject(-glassWidth/2, -height-10, -glassLength/2);
      const glass2 = isoProject(glassWidth/2, -height-10, -glassLength/2);
      const glass3 = isoProject(glassWidth/2, -height-10, glassLength/2);
      const glass4 = isoProject(-glassWidth/2, -height-10, glassLength/2);
      ctx.moveTo(glass1.x, glass1.y);
      ctx.lineTo(glass2.x, glass2.y);
      ctx.lineTo(glass3.x, glass3.y);
      ctx.lineTo(glass4.x, glass4.y);
      ctx.closePath();
      ctx.fill();
    }

    // Glasfläche selbst mit verbesserter Darstellung
    let glassColor = 'rgba(125, 211, 252, 0.5)';
    switch (state.glassType) {
      case 'klar':
        glassColor = 'rgba(56, 189, 248, 0.6)';
        break;
      case 'matt':
        glassColor = 'rgba(14, 165, 233, 0.7)';
        break;
      case 'selbstreinigend':
        glassColor = 'rgba(125, 211, 252, 0.5)';
        break;
    }

    // Farbiger Rahmen um die Glasfläche
    ctx.strokeStyle = '#0ea5e9';
    ctx.lineWidth = 3;
    ctx.shadowColor = 'rgba(14, 165, 233, 0.4)';
    ctx.shadowBlur = 15;
    
    if (isCircular) {
      // Für CIRCULAR: Zeichne eine Ellipse statt Rechteck (isometrische Projektion eines Kreises)
      const radius = width / 2;
      const centerPoint = isoProject(0, -height-10, 0);
      const radiusX = radius * Math.cos(isoAngle) * scale;
      const radiusY = radius * Math.sin(isoAngle) * scale;
      
      ctx.beginPath();
      ctx.ellipse(centerPoint.x, centerPoint.y, radiusX + 2, radiusY + 2, 0, 0, Math.PI * 2);
      ctx.stroke();
      
      ctx.fillStyle = glassColor;
      ctx.beginPath();
      ctx.ellipse(centerPoint.x, centerPoint.y, radiusX, radiusY, 0, 0, Math.PI * 2);
      ctx.fill();
    } else {
      // Für FIXED/LINEAR: Rechteck wie bisher
      const glass5 = isoProject(-width/2, -height-10, -length/2);
      const glass6 = isoProject(width/2, -height-10, -length/2);
      const glass7 = isoProject(width/2, -height-10, length/2);
      const glass8 = isoProject(-width/2, -height-10, length/2);
      
      ctx.beginPath();
      ctx.moveTo(glass5.x - 2, glass5.y - 2);
      ctx.lineTo(glass6.x + 2, glass6.y - 2);
      ctx.lineTo(glass7.x + 2, glass7.y + 2);
      ctx.lineTo(glass8.x - 2, glass8.y + 2);
      ctx.closePath();
      ctx.stroke();
      
      ctx.fillStyle = glassColor;
      ctx.beginPath();
      ctx.moveTo(glass5.x, glass5.y);
      ctx.lineTo(glass6.x, glass6.y);
      ctx.lineTo(glass7.x, glass7.y);
      ctx.lineTo(glass8.x, glass8.y);
      ctx.closePath();
      ctx.fill();
    }
    
    ctx.shadowColor = 'transparent';
    ctx.shadowBlur = 0;
    
    // Rohbauöffnung zeichnen, wenn aktiviert
    if (showRoughOpening && isFixed) {
      // Berechne die Punkte für die Rohbauöffnung (größer als das Bestellmaß)
      const roughGlass1 = isoProject(-roughOpeningWidth/2, -height-15, -roughOpeningLength/2);
      const roughGlass2 = isoProject(roughOpeningWidth/2, -height-15, -roughOpeningLength/2);
      const roughGlass3 = isoProject(roughOpeningWidth/2, -height-15, roughOpeningLength/2);
      const roughGlass4 = isoProject(-roughOpeningWidth/2, -height-15, roughOpeningLength/2);
      
      // Zeichne gestrichelte rote Linien für die Rohbauöffnung
      ctx.strokeStyle = '#ef4444';
      ctx.lineWidth = 2;
      ctx.setLineDash([5, 5]);
      
      // Zeichne das Rechteck der Rohbauöffnung
      ctx.beginPath();
      ctx.moveTo(roughGlass1.x, roughGlass1.y);
      ctx.lineTo(roughGlass2.x, roughGlass2.y);
      ctx.lineTo(roughGlass3.x, roughGlass3.y);
      ctx.lineTo(roughGlass4.x, roughGlass4.y);
      ctx.closePath();
      ctx.stroke();
      
      // Beschriftung für Rohbauöffnung hinzufügen
      ctx.fillStyle = '#ef4444';
      ctx.font = 'bold 16px Arial';
      
      // Hintergrund für bessere Lesbarkeit
      const textWidth = ctx.measureText('Rohbauöffnung').width;
      ctx.fillStyle = 'rgba(255, 255, 255, 0.7)';
      ctx.fillRect(
        (roughGlass1.x + roughGlass3.x) / 2 - textWidth/2 - 5,
        (roughGlass1.y + roughGlass3.y) / 2 - 35,
        textWidth + 10,
        25
      );
      
      ctx.fillStyle = '#ef4444';
      ctx.fillText('Rohbauöffnung', (roughGlass1.x + roughGlass3.x) / 2, (roughGlass1.y + roughGlass3.y) / 2 - 20);
      
      ctx.setLineDash([]);
    }

    // Animierte Reflexion auf der Glasfläche
    if (isCircular) {
      // Für CIRCULAR: Animierte radiale Reflexion
      const radius = width / 2;
      const centerPoint = isoProject(0, -height-10, 0);
      const gradientRadius = radius * Math.cos(isoAngle) * scale;
      const animAngle = (animationFrame / 200) * Math.PI * 2;
      
      const gradient = ctx.createRadialGradient(
        centerPoint.x + Math.cos(animAngle) * gradientRadius/3,
        centerPoint.y + Math.sin(animAngle) * gradientRadius/3,
        0,
        centerPoint.x,
        centerPoint.y,
        gradientRadius
      );
      gradient.addColorStop(0, 'rgba(255, 255, 255, 0.6)');
      gradient.addColorStop(0.3, 'rgba(255, 255, 255, 0.3)');
      gradient.addColorStop(1, 'rgba(255, 255, 255, 0)');
      ctx.fillStyle = gradient;
      
      ctx.beginPath();
      const radiusX = radius * Math.cos(isoAngle) * scale;
      const radiusY = radius * Math.sin(isoAngle) * scale;
      ctx.ellipse(centerPoint.x, centerPoint.y, radiusX, radiusY, 0, 0, Math.PI * 2);
      ctx.fill();
    } else {
      // Für FIXED/LINEAR: Animierte lineare Reflexion
      const glass5 = isoProject(-width/2, -height-10, -length/2);
      const glass7 = isoProject(width/2, -height-10, length/2);
      const animOffset = (animationFrame / 200) * (Math.abs(glass7.x - glass5.x) + Math.abs(glass7.y - glass5.y));
      
      const gradient = ctx.createLinearGradient(
        glass5.x - animOffset,
        glass5.y - animOffset,
        glass7.x + animOffset,
        glass7.y + animOffset
      );
      gradient.addColorStop(0, 'rgba(255, 255, 255, 0)');
      gradient.addColorStop(0.4, 'rgba(255, 255, 255, 0)');
      gradient.addColorStop(0.5, 'rgba(255, 255, 255, 0.6)');
      gradient.addColorStop(0.6, 'rgba(255, 255, 255, 0)');
      gradient.addColorStop(1, 'rgba(255, 255, 255, 0)');
      ctx.fillStyle = gradient;
      
      ctx.beginPath();
      const glass5_2 = isoProject(-width/2, -height-10, -length/2);
      const glass6_2 = isoProject(width/2, -height-10, -length/2);
      const glass7_2 = isoProject(width/2, -height-10, length/2);
      const glass8_2 = isoProject(-width/2, -height-10, length/2);
      ctx.moveTo(glass5_2.x, glass5_2.y);
      ctx.lineTo(glass6_2.x, glass6_2.y);
      ctx.lineTo(glass7_2.x, glass7_2.y);
      ctx.lineTo(glass8_2.x, glass8_2.y);
      ctx.closePath();
      ctx.fill();
    }
    
    // Hover-Effekt für 3D-Ansicht
    if (isHovering) {
      if (isCircular) {
        const radius = width / 2;
        const centerPoint = isoProject(0, -height-10, 0);
        const radiusX = radius * Math.cos(isoAngle) * scale;
        const radiusY = radius * Math.sin(isoAngle) * scale;
        
        ctx.beginPath();
        ctx.ellipse(centerPoint.x, centerPoint.y, radiusX, radiusY, 0, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(14, 165, 233, 0.2)';
        ctx.fill();
      } else {
        const glass5 = isoProject(-width/2, -height-10, -length/2);
        const glass6 = isoProject(width/2, -height-10, -length/2);
        const glass7 = isoProject(width/2, -height-10, length/2);
        const glass8 = isoProject(-width/2, -height-10, length/2);
        
        ctx.beginPath();
        ctx.moveTo(glass5.x, glass5.y);
        ctx.lineTo(glass6.x, glass6.y);
        ctx.lineTo(glass7.x, glass7.y);
        ctx.lineTo(glass8.x, glass8.y);
        ctx.closePath();
        ctx.fillStyle = 'rgba(14, 165, 233, 0.2)';
        ctx.fill();
      }
    }

    // Maße anzeigen
    ctx.fillStyle = '#1e293b';
    ctx.font = 'bold 16px Arial';
    ctx.textAlign = 'center';

    // Maße für CIRCULAR anders anzeigen
    if (isCircular) {
      // Durchmesser
      const diameterMidPoint = isoProject(0, 0, -width/2 - 50);
      const diameterText = `Ø ${width} mm`;
      const diameterTextWidth = ctx.measureText(diameterText).width;
      
      ctx.fillStyle = 'rgba(255, 255, 255, 0.7)';
      ctx.fillRect(diameterMidPoint.x - diameterTextWidth/2 - 5, diameterMidPoint.y + 5, diameterTextWidth + 10, 25);
      
      ctx.fillStyle = '#1e293b';
      ctx.fillText(diameterText, diameterMidPoint.x, diameterMidPoint.y + 20);
    } else {
      // Breite und Länge für FIXED/LINEAR
      const widthMidPoint = isoProject(0, 0, -length/2 - 50);
      if (showRoughOpening && isFixed) {
        // Hintergrund für bessere Lesbarkeit
        const roughWidthText = `${roughOpeningWidth} mm (Rohbau)`;
        const widthText = `${width} mm (Bestellmaß)`;
        const roughWidthTextWidth = ctx.measureText(roughWidthText).width;
        const widthTextWidth = ctx.measureText(widthText).width;
        
        ctx.fillStyle = 'rgba(255, 255, 255, 0.7)';
        ctx.fillRect(widthMidPoint.x - Math.max(roughWidthTextWidth, widthTextWidth)/2 - 5, widthMidPoint.y + 5, Math.max(roughWidthTextWidth, widthTextWidth) + 10, 50);
        
        ctx.fillStyle = '#ef4444';
        ctx.fillText(roughWidthText, widthMidPoint.x, widthMidPoint.y + 20);
        ctx.fillStyle = '#1e293b';
        ctx.fillText(widthText, widthMidPoint.x, widthMidPoint.y + 40);
      } else {
        ctx.fillText(`${width} mm (Breite)`, widthMidPoint.x, widthMidPoint.y + 20);
      }

      // Länge
      const lengthMidPoint = isoProject(width/2 + 50, 0, 0);
      if (showRoughOpening && isFixed) {
        // Hintergrund für bessere Lesbarkeit
        const roughLengthText = `${roughOpeningLength} mm (Rohbau)`;
        const lengthText = `${length} mm (Bestellmaß)`;
        const roughLengthTextWidth = ctx.measureText(roughLengthText).width;
        const lengthTextWidth = ctx.measureText(lengthText).width;
        
        ctx.fillStyle = 'rgba(255, 255, 255, 0.7)';
        ctx.fillRect(lengthMidPoint.x - Math.max(roughLengthTextWidth, lengthTextWidth)/2 - 5, lengthMidPoint.y - 15, Math.max(roughLengthTextWidth, lengthTextWidth) + 10, 50);
        
        ctx.fillStyle = '#ef4444';
        ctx.fillText(roughLengthText, lengthMidPoint.x, lengthMidPoint.y);
        ctx.fillStyle = '#1e293b';
        ctx.fillText(lengthText, lengthMidPoint.x, lengthMidPoint.y + 20);
      } else {
        ctx.fillText(`${length} mm (Tiefe)`, lengthMidPoint.x, lengthMidPoint.y);
      }
    }

    // Höhe
    const heightMidPoint = isoProject(-width/2 - 60, -height/2, -length/2);
    
    // Hintergrund für bessere Lesbarkeit
    const heightText = `${height} mm (Höhe)`;
    const heightTextWidth = ctx.measureText(heightText).width;
    
    ctx.fillStyle = 'rgba(255, 255, 255, 0.7)';
    ctx.fillRect(heightMidPoint.x - heightTextWidth/2 - 5, heightMidPoint.y - 15, heightTextWidth + 10, 25);
    
    ctx.fillStyle = '#1e293b';
    ctx.fillText(heightText, heightMidPoint.x, heightMidPoint.y);

  }, [state, isCircular, isLinear, activeView, showRoughOpening, showDimensions, isFixed, animationFrame, isHovering]);

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <div className="flex justify-between items-start mb-4">
        <h3 className="text-lg font-medium text-gray-900">Produktvorschau</h3>
        <button
          type="button"
          onClick={() => {
            // DIREKTE DOM-MANIPULATION - garantiert sichtbar!
            const productName = state.product || 'SkyVision FIXED';
            const modalHTML = `
              <div id="direct-modal" style="
                position: fixed;
                top: 0;
                left: 0;
                right: 0;
                bottom: 0;
                background-color: rgba(0, 0, 0, 0.5);
                z-index: 999999;
                display: flex;
                align-items: center;
                justify-content: center;
              ">
                <div style="
                  background-color: white;
                  border-radius: 8px;
                  max-width: 500px;
                  width: 90%;
                  max-height: 80vh;
                  overflow-y: auto;
                  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
                ">
                  <div style="
                    background-color: #77b829;
                    color: white;
                    padding: 20px;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                  ">
                    <h3 style="margin: 0; font-size: 18px; font-weight: 600; color: white;">
                      Produktinformationen - ${productName}
                    </h3>
                    <button onclick="document.getElementById('direct-modal').remove()" style="
                      background: transparent;
                      border: none;
                      color: white;
                      font-size: 24px;
                      cursor: pointer;
                      padding: 0;
                      width: 30px;
                      height: 30px;
                    ">×</button>
                  </div>
                  
                  <div style="padding: 20px;">
                    <div style="margin-bottom: 20px; background-color: #f9fafb; padding: 15px; border-radius: 8px;">
                      <h4 style="margin: 0 0 10px 0; font-weight: 600; color: #374151;">Aufmaß-Hilfe</h4>
                      <div style="display: flex; flex-direction: column; gap: 8px;">
                        <a href="#" style="color: #475569; text-decoration: none;">Video-Anleitung</a>
                        <a href="#" style="color: #475569; text-decoration: none;">PDF-Anleitung</a>
                      </div>
                    </div>
                    
                    <div style="margin-bottom: 20px; background-color: #f9fafb; padding: 15px; border-radius: 8px;">
                      <h4 style="margin: 0 0 10px 0; font-weight: 600; color: #374151;">Ausschreibungstexte</h4>
                      <a href="#" style="color: #475569; text-decoration: none;">Ausschreibungstext herunterladen</a>
                    </div>
                    
                    <div style="background-color: #f9fafb; padding: 15px; border-radius: 8px;">
                      <h4 style="margin: 0 0 10px 0; font-weight: 600; color: #374151;">Weitere Informationen</h4>
                      <a href="#" style="color: #475569; text-decoration: none;">Produktinformationen</a>
                    </div>
                  </div>
                  
                  <div style="background-color: #f9fafb; padding: 15px; border-top: 1px solid #e5e7eb;">
                    <p style="margin: 0; font-size: 12px; color: #6b7280;">
                      Klicken Sie auf die jeweiligen Links, um die entsprechenden Dokumente zu öffnen oder herunterzuladen.
                    </p>
                  </div>
                </div>
              </div>
            `;
            
            document.body.insertAdjacentHTML('beforeend', modalHTML);
          }}
          className="text-blue-600 hover:text-blue-800 bg-white rounded-full p-1 border border-gray-300"
          style={{ minWidth: '32px', minHeight: '32px' }}
        >
          <InformationCircleIcon className="h-6 w-6" />
        </button>
      </div>

      <div className="flex flex-col gap-4 mb-4">
        {/* Ansichts-Auswahl */}
        <div className="flex justify-center gap-4">
          <button 
            className={`px-4 py-2 rounded-lg ${activeView === 'top' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'}`}
            onClick={() => setActiveView('top')}
          >
            Draufsicht
          </button>
          <button 
            className={`px-4 py-2 rounded-lg ${activeView === 'complete' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'}`}
            onClick={() => setActiveView('complete')}
          >
            Komplettansicht
          </button>
        </div>
        
        {/* Darstellungsoptionen */}
        <div className="border-t border-gray-200 pt-4">
          <h4 className="text-sm font-medium text-gray-700 mb-3">Darstellungsoptionen</h4>
          
          <div className="grid grid-cols-2 gap-3">
            {/* Maße anzeigen */}
            <label className="inline-flex items-center cursor-pointer">
              <input 
                type="checkbox" 
                checked={showDimensions}
                onChange={() => setShowDimensions(!showDimensions)}
                className="sr-only peer"
              />
              <div className="relative w-11 h-6 bg-gray-200 rounded-full peer peer-checked:bg-blue-600 peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all"></div>
              <span className="ml-2 text-sm text-gray-700">Maße</span>
            </label>

            {/* Rahmen anzeigen */}
            <label className="inline-flex items-center cursor-pointer">
              <input 
                type="checkbox" 
                checked={showFrame}
                onChange={() => setShowFrame(!showFrame)}
                className="sr-only peer"
              />
              <div className="relative w-11 h-6 bg-gray-200 rounded-full peer peer-checked:bg-blue-600 peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all"></div>
              <span className="ml-2 text-sm text-gray-700">Rahmen</span>
            </label>

            {/* Glasfläche anzeigen */}
            <label className="inline-flex items-center cursor-pointer">
              <input 
                type="checkbox" 
                checked={showGlass}
                onChange={() => setShowGlass(!showGlass)}
                className="sr-only peer"
              />
              <div className="relative w-11 h-6 bg-gray-200 rounded-full peer peer-checked:bg-blue-600 peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all"></div>
              <span className="ml-2 text-sm text-gray-700">Glasfläche</span>
            </label>

            {/* Zubehör anzeigen */}
            <label className="inline-flex items-center cursor-pointer">
              <input 
                type="checkbox" 
                checked={showAccessories}
                onChange={() => setShowAccessories(!showAccessories)}
                className="sr-only peer"
              />
              <div className="relative w-11 h-6 bg-gray-200 rounded-full peer peer-checked:bg-blue-600 peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all"></div>
              <span className="ml-2 text-sm text-gray-700">Zubehör</span>
            </label>

            {/* Raster anzeigen */}
            <label className="inline-flex items-center cursor-pointer">
              <input 
                type="checkbox" 
                checked={showGrid}
                onChange={() => setShowGrid(!showGrid)}
                className="sr-only peer"
              />
              <div className="relative w-11 h-6 bg-gray-200 rounded-full peer peer-checked:bg-blue-600 peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all"></div>
              <span className="ml-2 text-sm text-gray-700">Raster</span>
            </label>
          </div>
        </div>
        
        {/* Rohbauöffnung (nur für FIXED) */}
        {isFixed && (
          <div className="border-t border-gray-200 pt-3">
            <div className="flex justify-center items-center gap-2">
              <label className="inline-flex items-center cursor-pointer">
                <input 
                  type="checkbox" 
                  checked={showRoughOpening}
                  onChange={() => setShowRoughOpening(!showRoughOpening)}
                  className="sr-only peer"
                />
                <div className="relative w-11 h-6 bg-gray-200 rounded-full peer peer-checked:bg-blue-600 peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all"></div>
                <span className="ml-2 text-sm font-medium text-gray-700">
                  {showRoughOpening ? 'Rohbauöffnung wird angezeigt' : 'Bestellmaß wird angezeigt'}
                </span>
              </label>
              <div className="relative inline-block">
                <button 
                  type="button"
                  onClick={() => setShowTooltip({...showTooltip, visualizer: !showTooltip.visualizer})}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <InformationCircleIcon className="h-5 w-5" />
                </button>
                {showTooltip?.visualizer && (
                  <div className="absolute z-10 w-64 p-3 bg-white rounded-lg shadow-lg border border-gray-200 text-xs text-gray-600 right-0 mt-1">
                    <p><strong>Bestellmaß:</strong> Die tatsächlichen Maße des Oberlichts.</p>
                    <p className="mt-1"><strong>Rohbauöffnung:</strong> Die Maße der Öffnung in der Decke (Bestellmaß + 30 mm).</p>
                    <div className="absolute w-3 h-3 bg-white border-t border-l border-gray-200 transform -translate-y-1/2 rotate-45 right-4 -top-1"></div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {activeView === 'top' && (
      <div className="relative">
          <h4 className="text-md font-medium text-gray-700 mb-2">Draufsicht</h4>
        <canvas
          ref={canvasRef}
          className="w-full cursor-pointer transition-transform hover:scale-[1.02]"
          style={{ maxWidth: '600px', margin: '0 auto' }}
          onMouseEnter={() => setIsHovering(true)}
          onMouseLeave={() => setIsHovering(false)}
        />
      </div>
      )}
      
      {activeView === 'side' && (
        <div className="relative">
          <h4 className="text-md font-medium text-gray-700 mb-2">Seitenansicht mit Höhe des Aufsatzkranzes</h4>
          <canvas
            ref={sideViewCanvasRef}
            className="w-full cursor-pointer transition-transform hover:scale-[1.02]"
            style={{ maxWidth: '600px', margin: '0 auto' }}
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
          />
        </div>
      )}
      
      {activeView === 'complete' && (
        <div className="relative">
          <h4 className="text-md font-medium text-gray-700 mb-2">3D Komplettansicht</h4>
          <canvas
            ref={completeViewCanvasRef}
            className="w-full cursor-pointer transition-transform hover:scale-[1.02]"
            style={{ maxWidth: '600px', margin: '0 auto' }}
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
          />
        </div>
      )}

{/* Modal durch direkte DOM-Manipulation ersetzt */}
    </div>
  );
} 